// supabase/functions/build-room-bridge/handler.ts
//
// The bridge between the command center page and the board (item #701, part of
// #700; TCSD-0412). The items stay on GitHub as the record; this function holds
// the key so no key ever sits in a browser.
//
// Reads (Run 1, plus two in Run 2):
//   GET ?view=items — every open item, with its parent and its children, and
//                     (item #703) its newest Touches note, read
//   GET ?view=runs  — the Build Room workflows' runs (in progress, or in the
//                     last 24 hours) and the list of Build Room workflows
//   GET ?view=whoami — may this caller press the buttons? (the write checks,
//                     minus the item)
//   GET ?view=feedback-links&ids=AFB-…,… — for each feedback id, the CLOSED
//                     items whose body mentions it (feedback-intake's rule)
//
// Writes (Run 2, item #702): POST { action, item, ... } — one action per call.
// Item #853 (pick 3A) added two: `note` (Ben's words on any open item, no label
// moves) and `file` (a new item, { title, words, parts, route }, refused here by
// intake's own rule before it is filed). The Claude connector
// (build-room-connector) calls this same handle(); no rule is repeated there.
// Every write passes, in order: signed in (401) · superuser (403) · the KEY'S
// OWNER (403, "Only Ben acts from here. You can see everything." — the caller's
// app email must be the public profile email of the GitHub account the key belongs to,
// both read fresh on every call) · the item is where the action expects it
// (409, read fresh from GitHub, never the page's copy). Then the comment, then
// the labels. A label change that fails after its comment posted is reported
// as a failure and noted on the item; a partial write is never a success.
// Feedback is NOT proxied: the page reads app_feedback through the app's own
// client, where a superuser already has access. Nothing is cached.
//
// The caller check is copied from facility-summary: 401 with no or a malformed
// token (the gateway's verify_jwt has already checked the signature), and 403
// unless is_superuser() is true for the caller, asked through a client that
// carries the caller's own token.
//
// ⛔ NEVER AN EMPTY LIST FOR A FAILURE. A missing key is 503
// {"error":"bridge key missing"}; a key GitHub refuses is
// {"error":"bridge key refused"} with GitHub's status; a board that could not
// be read in full is an error, never a shorter list.
//
// This file is pure — no Deno, no URL imports — so the tests beside it and the
// smoke suite can run it in Node. index.ts wires it to Deno.serve.

export const REPO_OWNER = 'Terminal-Cleaning-Services';
export const REPO_NAME = 'tcs-app';
const GITHUB_API = 'https://api.github.com';
const COMMENT_EXCERPT_CHARS = 300;
// How far back the bridge looks for an item's write-up. The Assessor posts it, and
// anything said after it is usually a line or two, so 15 comments reaches it with
// room to spare. Not found in those 15 is said as such, never as "no write-up".
export const WRITE_UP_LOOKBACK = 15;
const RUN_WINDOW_HOURS = 24;

const ALLOWED_ORIGINS = new Set([
  'https://app.terminalcleaning.com',
  'https://tcs-erp-fd3ae.web.app',
  'http://localhost:5173',
]);

export function corsHeaders(origin: string | null): Record<string, string> {
  const o = origin && ALLOWED_ORIGINS.has(origin) ? origin : 'https://app.terminalcleaning.com';
  return {
    'Access-Control-Allow-Origin': o,
    'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
    'Access-Control-Max-Age': '86400',
  };
}

export type SuperuserCheck =
  | { ok: true; value: boolean }
  | { ok: false; status: number; message: string };

/** The caller's own app email, or null when their profile has none. */
export type EmailCheck =
  | { ok: true; value: string | null }
  | { ok: false; status: number; message: string };

export interface BridgeDeps {
  /** Reads a function secret. index.ts passes Deno.env.get. */
  getEnv: (name: string) => string | undefined;
  /** Asks is_superuser() as the caller. index.ts uses a user-scoped client. */
  isSuperuser: (authHeader: string) => Promise<SuperuserCheck>;
  /**
   * The caller's email from their own user_profiles row, read AS the caller and
   * keyed on the token's `sub` (the gateway has verified the signature). Only
   * the writes and whoami need it; without it they answer 500, never "yes".
   */
  callerEmail?: (authHeader: string, uid: string) => Promise<EmailCheck>;
  fetch: typeof fetch;
  /**
   * Opens a zip: file name -> bytes. index.ts passes fflate's unzipSync. Only the
   * demo shelf needs it; without it the two demo views answer 500, never "none".
   */
  unzip?: (bytes: Uint8Array) => Record<string, Uint8Array>;
}

// ----- what the page receives --------------------------------------------------

export interface BoardChild {
  number: number;
  title: string;
  state: 'open' | 'closed';
  url: string;
  labels: string[];
}

export interface BoardItem {
  number: number;
  title: string;
  url: string;
  body: string;
  labels: string[];
  created_at: string;
  updated_at: string;
  assignees: string[];
  /**
   * When the newest of the current assignees was added (GitHub's own "assigned"
   * event). A pin is GitHub's "assigned to" (#702 decision 6), so this is the
   * pin's time, for "newest pin first". Null when nobody is assigned.
   */
  pinned_at: string | null;
  parent: { number: number; title: string } | null;
  children: BoardChild[];
  /** GitHub's own count of sub-items, and how many of them are closed. */
  children_total: number;
  children_closed: number;
  latest_comment: { excerpt: string; truncated: boolean; created_at: string; author: string | null } | null;
  /**
   * The newest decision write-up on the item, in full: a comment with a
   * "## Recommendation" heading (the Assessor's shape — where it came from, what
   * was checked, options, recommendation, if approved). Null when none of the
   * last WRITE_UP_LOOKBACK comments is one.
   */
  write_up: { body: string; created_at: string; author: string | null } | null;
  /** True when the item has more comments than were searched and none of them was a write-up. */
  write_up_maybe_older: boolean;
  /**
   * The item's newest Touches note, read (item #703): which other open items it
   * settles, waits on or shares records with, and the parts of the app it names.
   * Null when none of the last WRITE_UP_LOOKBACK comments is one — never guessed.
   * Filled by the board read; a single-item read after a write carries null (the
   * page re-reads the board after every write).
   */
  touches: Touches | null;
  /** The checker's passes on the item's pull request and its newest word, read from its comments (item #1013). */
  checker: Checker;
  /** The Builder's own note that a run finished without pushing its work, newest (item #1013). */
  builder_note: { created_at: string; run_url: string | null } | null;
  /** How Ben approved it, from the command center's Approved comment, newest (item #855). */
  approval: { created_at: string; words: string; route: 'cowork' | 'builder' | null } | null;
  /** A fuller prompt a session posted on the item ("## Dispatch"), newest (item #855). */
  dispatch: { body: string; created_at: string } | null;
  /** How many times Ben has sent it back to the Builder (item #1013). */
  send_backs: number;
}

export interface ItemsView {
  items: BoardItem[];
  /** GitHub's own count of open items. The list above is checked against it. */
  open_count: number;
  read_at: string;
}

// ----- Touches notes (item #703; TCSD-0419, TCSD-0423) -------------------------------
//
// One comment per item, written only by the Assessor, whose first line is exactly
// "## Touches". Then one line per link and the parts line:
//
//   ## Touches
//   - Settles #123 — <what about #123 this item's answer decides>
//   - Waits on #45 — <reason>
//   - Shares records with #67 — <which records>
//   Parts of the app: <catalog name>, <catalog name>
//
// or "- Touches no other open item." then the parts line. A line in no shape is
// ignored and COUNTED, never guessed. A link to anything but an open item that is
// not a project (closed, a project, the item itself) is dropped at read time.
// Links are read both ways by whoever reads them: "A settles B" is "B waits on A".
//
// The parts are returned exactly as written. Which of them the catalog knows is
// decided by whoever holds the catalog — the page (derive.ts, its copy locked to the catalog
// by derive.test.ts) and the board script (catalog.mjs). The bridge's key reads issues and
// runs; it is not given the repository's files. Counting who touches whom is derive.ts's.

export interface TouchLink {
  number: number;
  reason: string;
}

export interface Touches {
  settles: TouchLink[];
  waits_on: TouchLink[];
  shares: TouchLink[];
  /** The names on the "Parts of the app:" line, exactly as written; [] for "none". */
  parts: string[];
  note_at: string;
  malformed_lines: number;
}

export const TOUCHES_HEADING = '## Touches';
const TOUCH_LINK = /^-\s+(Settles|Waits on|Shares records with)\s+#(\d+)\s+—\s+(\S.*)$/;
const TOUCH_NONE = /^-\s+Touches no other open item\.$/;
const TOUCH_PARTS = /^Parts of the app:\s*(.*)$/;

/** A Touches note's first line is exactly "## Touches" (trailing spaces aside). */
export function isTouchesNote(body: unknown): boolean {
  return typeof body === 'string' && body.split('\n', 1)[0].replace(/\s+$/, '') === TOUCHES_HEADING;
}

/** The newest Touches note among some comments (GitHub's own shape), or null. */
export function newestTouchesNote<C extends { body?: string | null; createdAt?: string }>(comments: readonly C[]): C | null {
  let best: C | null = null;
  for (const c of comments) {
    if (!isTouchesNote(c?.body)) continue;
    if (!best || String(c.createdAt ?? '') > String(best.createdAt ?? '')) best = c;
  }
  return best;
}

/** Reads one note. `open` is the numbers of the OPEN items that are NOT projects in the same read. Pure. */
export function parseTouchesNote(body: string, noteAt: string, self: number, open: ReadonlySet<number>): Touches {
  const t: Touches = { settles: [], waits_on: [], shares: [], parts: [], note_at: noteAt, malformed_lines: 0 };
  let sawParts = false;
  let sawNone = false;
  for (const raw of body.split('\n').slice(1)) {
    const line = raw.replace(/\s+$/, '');
    if (line === '') continue;
    const link = TOUCH_LINK.exec(line);
    if (link) {
      const n = Number(link[2]);
      if (n === self || !open.has(n)) continue; // closed, a project, or itself: dropped, not malformed
      const bucket = link[1] === 'Settles' ? t.settles : link[1] === 'Waits on' ? t.waits_on : t.shares;
      if (!bucket.some((e) => e.number === n)) bucket.push({ number: n, reason: link[3].trim() });
      continue;
    }
    if (TOUCH_NONE.test(line) && !sawNone) {
      sawNone = true;
      continue;
    }
    const parts = TOUCH_PARTS.exec(line);
    if (parts && !sawParts && parts[1].trim() !== '') {
      sawParts = true;
      const named = parts[1].trim();
      if (named === 'none') continue;
      for (const p of named.split(',').map((s) => s.trim()).filter(Boolean)) if (!t.parts.includes(p)) t.parts.push(p);
      continue;
    }
    t.malformed_lines += 1; // includes a second parts line and a second "touches no other"
  }
  return t;
}

export interface BoardRun {
  id: number;
  workflow: string;
  title: string;
  status: string;
  conclusion: string | null;
  event: string;
  started_at: string;
  url: string;
}

/** Which items have a demo on the shelf (item #827). */
export interface DemosView {
  items: number[];
  /** When the shelf was last rebuilt; null when there is no shelf yet. */
  built_at: string | null;
  read_at: string;
}

/** One demo, as the design bot drew it. */
export interface DemoView {
  item: number;
  html: string;
}

export interface RunsView {
  runs: BoardRun[];
  workflows: Array<{ name: string; state: string }>;
  since: string;
  read_at: string;
}

// ----- failures that must reach the page as words -------------------------------

// Plain fields, not constructor parameter properties: Node's type stripping
// (which the smoke suite relies on) cannot erase those.
export class GitHubRefused extends Error {
  status: number;
  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
  }
}
export class GitHubRateLimited extends Error {}
export class BoardIncomplete extends Error {}

function json(body: unknown, status: number, headers: Record<string, string>): Response {
  return new Response(JSON.stringify(body), { status, headers });
}

function decodeJwtPayload(authHeader: string): Record<string, unknown> {
  const jwt = authHeader.slice('bearer '.length).trim();
  const parts = jwt.split('.');
  if (parts.length !== 3) throw new Error('malformed jwt');
  const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
  return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
}

export async function handle(req: Request, deps: BridgeDeps): Promise<Response> {
  const origin = req.headers.get('origin');
  const headers = { ...corsHeaders(origin), 'Content-Type': 'application/json' };

  if (req.method === 'OPTIONS') return new Response('ok', { headers });
  if (req.method !== 'GET' && req.method !== 'POST') return json({ error: 'method_not_allowed' }, 405, headers);

  // 401 — no token, or one that is not a JWT at all. Same shape as facility-summary.
  const authHeader = req.headers.get('Authorization') || '';
  if (!authHeader.toLowerCase().startsWith('bearer ')) {
    return json({ error: 'missing_auth' }, 401, headers);
  }
  let claims: Record<string, unknown>;
  try {
    claims = decodeJwtPayload(authHeader);
  } catch (_e) {
    return json({ error: 'invalid_jwt' }, 401, headers);
  }

  // 403 — anyone who is not a superuser, asked as the caller.
  const su = await deps.isSuperuser(authHeader);
  if (!su.ok) return json({ error: su.message }, su.status, headers);
  if (!su.value) return json({ error: 'forbidden' }, 403, headers);

  // 503 — the key is not stored. Never an empty board.
  const key = deps.getEnv('GITHUB_BOARD_TOKEN');
  if (!key) return json({ error: 'bridge key missing' }, 503, headers);

  const caller = { authHeader, uid: typeof claims.sub === 'string' ? claims.sub : '' };
  try {
    if (req.method === 'POST') return await handleWrite(req, deps, key, caller, headers);
    const params = new URL(req.url).searchParams;
    const view = params.get('view');
    if (view === 'items') return json(await readItems(key, deps.fetch), 200, headers);
    if (view === 'runs') return json(await readRuns(key, deps.fetch), 200, headers);
    if (view === 'whoami') {
      const who = await ownerCheck(deps, key, caller);
      return json(who.ok ? { canAct: true, login: who.login } : { canAct: false, reason: who.message }, who.ok || who.status === 403 ? 200 : who.status, headers);
    }
    if (view === 'feedback-links') {
      const ids = (params.get('ids') ?? '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
      if (ids.length === 0) return json({ error: 'bad request', message: 'Name at least one feedback id.' }, 400, headers);
      if (ids.length > FEEDBACK_LINK_MAX) {
        return json({ error: 'bad request', message: `At most ${FEEDBACK_LINK_MAX} feedback ids per call; ${ids.length} were sent.` }, 400, headers);
      }
      const bad = ids.filter((i) => !/^AFB-\d+$/.test(i));
      if (bad.length) return json({ error: 'bad request', message: `Not feedback ids: ${bad.join(', ')}.` }, 400, headers);
      return json(await readFeedbackLinks(key, deps.fetch, ids), 200, headers);
    }
    if (view === 'demos' || view === 'demo') {
      if (!deps.unzip) return json({ error: 'demo shelf unreadable', detail: 'The bridge cannot open the shelf here.' }, 500, headers);
      const shelf = await readShelf(key, deps.fetch, deps.unzip);
      if (view === 'demos') {
        const demos: DemosView = { items: shelf.items, built_at: shelf.built_at, read_at: new Date().toISOString() };
        return json(demos, 200, headers);
      }
      const n = Number(params.get('item'));
      if (!Number.isInteger(n) || n <= 0) return json({ error: 'bad request', message: 'Name the item by its number.' }, 400, headers);
      const html = shelf.html(n);
      if (html === null) return json({ error: 'not found', message: `There is no demo on the shelf for item ${n}.` }, 404, headers);
      const demo: DemoView = { item: n, html };
      return json(demo, 200, headers);
    }
    return json({ error: 'unknown view', views: ['items', 'runs', 'whoami', 'feedback-links', 'demos', 'demo'] }, 400, headers);
  } catch (e) {
    if (e instanceof GitHubRefused) {
      return json({ error: 'bridge key refused', github_status: e.status, detail: e.message }, e.status, headers);
    }
    if (e instanceof GitHubRateLimited) {
      return json({ error: 'github rate limit reached', detail: e.message }, 503, headers);
    }
    if (e instanceof BoardIncomplete) {
      return json({ error: 'board read incomplete', detail: e.message }, 502, headers);
    }
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('[build-room-bridge] github read failed', msg);
    return json({ error: 'github unreachable', detail: msg.slice(0, 300) }, 502, headers);
  }
}

// ----- GitHub --------------------------------------------------------------------

// ----- The demo shelf (item #827) -------------------------------------------------
// The "Build Room — Demo shelf" workflow keeps every demo in its newest
// "demo-shelf" download (scripts/build-room/demo-shelf.mjs). The bridge reads it
// with the Actions read the key already has. No shelf yet reads as an empty
// shelf, said so by built_at: null — the workflow makes the first one.

export const SHELF = 'demo-shelf';

/** Pure: what a shelf zip holds. Exported for the tests. */
export function shelfFrom(files: Record<string, Uint8Array>): { items: number[]; built_at: string | null; html: (n: number) => string | null } {
  const text = (b: Uint8Array) => new TextDecoder().decode(b);
  const index = files['index.json'] ? JSON.parse(text(files['index.json'])) : null;
  const items = Object.keys(files)
    .map((f) => /^item-(\d+)\.html$/.exec(f)?.[1])
    .filter((x): x is string => !!x)
    .map(Number)
    .sort((a, b) => a - b);
  return {
    items,
    built_at: typeof index?.built_at === 'string' ? index.built_at : null,
    html: (n) => (files[`item-${n}.html`] ? text(files[`item-${n}.html`]) : null),
  };
}

async function readShelf(key: string, f: typeof fetch, unzip: (b: Uint8Array) => Record<string, Uint8Array>) {
  const listRes = await f(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}/actions/artifacts?name=${SHELF}&per_page=10`, { headers: ghHeaders(key) });
  await ghCheck(listRes);
  const list: Gql = await listRes.json();
  const newest = ((list.artifacts ?? []) as Gql[])
    .filter((a) => !a.expired)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
  if (!newest) return shelfFrom({});
  // GitHub answers with a redirect to short-lived storage. Follow it by hand so
  // the key is never sent to the storage host.
  const zipRes = await f(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}/actions/artifacts/${newest.id}/zip`, {
    headers: ghHeaders(key),
    redirect: 'manual',
  });
  let bytes: ArrayBuffer;
  if (zipRes.status >= 300 && zipRes.status < 400 && zipRes.headers.get('location')) {
    const blob = await f(zipRes.headers.get('location')!);
    if (!blob.ok) throw new Error(`The demo shelf could not be downloaded: ${blob.status}.`);
    bytes = await blob.arrayBuffer();
  } else {
    await ghCheck(zipRes);
    bytes = await zipRes.arrayBuffer();
  }
  return shelfFrom(unzip(new Uint8Array(bytes)));
}

function ghHeaders(key: string): Record<string, string> {
  return {
    Authorization: `Bearer ${key}`,
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'User-Agent': 'tcs-build-room-bridge',
  };
}

async function ghCheck(res: Response): Promise<void> {
  if (res.ok) return;
  const text = (await res.text()).slice(0, 300);
  if ((res.status === 403 || res.status === 429) && res.headers.get('x-ratelimit-remaining') === '0') {
    throw new GitHubRateLimited(`GitHub's rate limit is spent: ${text}`);
  }
  if (res.status === 401 || res.status === 403) throw new GitHubRefused(res.status, text);
  throw new Error(`GitHub answered ${res.status}: ${text}`);
}

// One item's fields, shared by the board read and the fresh single-item read a
// write makes, so the item a write returns has exactly the shape the page lists.
const ISSUE_FIELDS = `
        number title body url createdAt updatedAt state
        labels(first:50){totalCount nodes{name}}
        assignees(first:20){totalCount nodes{login}}
        timelineItems(itemTypes:[ASSIGNED_EVENT],last:20){nodes{... on AssignedEvent{createdAt assignee{... on User{login}}}}}
        parent{number title}
        subIssuesSummary{total completed}
        subIssues(first:100){totalCount nodes{number title state url labels(first:50){totalCount nodes{name}}}}
        comments(last:${WRITE_UP_LOOKBACK}){totalCount nodes{body createdAt author{login}}}`;

const ITEMS_QUERY = `query($owner:String!,$name:String!,$after:String){
  repository(owner:$owner,name:$name){
    issues(states:OPEN,first:50,after:$after,orderBy:{field:CREATED_AT,direction:ASC}){
      totalCount
      pageInfo{hasNextPage endCursor}
      nodes{${ISSUE_FIELDS}
      }
    }
  }
}`;

const ITEM_QUERY = `query($owner:String!,$name:String!,$number:Int!){
  repository(owner:$owner,name:$name){
    issue(number:$number){${ISSUE_FIELDS}
    }
  }
}`;

// deno-lint-ignore no-explicit-any
type Gql = any;

export async function readItems(key: string, f: typeof fetch): Promise<ItemsView> {
  const items: BoardItem[] = [];
  const notes = new Map<number, { body: string; createdAt: string }>();
  let after: string | null = null;
  let totalCount = -1;
  for (let page = 0; page < 40; page += 1) {
    const res = await f(`${GITHUB_API}/graphql`, {
      method: 'POST',
      headers: { ...ghHeaders(key), 'Content-Type': 'application/json', 'GraphQL-Features': 'sub_issues' },
      body: JSON.stringify({ query: ITEMS_QUERY, variables: { owner: REPO_OWNER, name: REPO_NAME, after } }),
    });
    await ghCheck(res);
    const body: Gql = await res.json();
    if (body.errors?.length) {
      const first = body.errors[0];
      const msg = String(first?.message ?? 'unknown GraphQL error');
      if (first?.type === 'FORBIDDEN' || /not accessible|permission/i.test(msg)) throw new GitHubRefused(403, msg);
      if (first?.type === 'RATE_LIMITED') throw new GitHubRateLimited(msg);
      throw new Error(`GitHub answered with an error: ${msg}`);
    }
    const conn = body.data?.repository?.issues;
    if (!conn) throw new Error('GitHub returned no issue list for the repository');
    totalCount = conn.totalCount;
    for (const n of conn.nodes as Gql[]) {
      const it = toItem(n);
      items.push(it);
      const note = newestTouchesNote((n.comments?.nodes ?? []) as Array<{ body?: string; createdAt?: string }>);
      if (note) notes.set(it.number, { body: String(note.body), createdAt: String(note.createdAt ?? '') });
    }
    if (!conn.pageInfo.hasNextPage) break;
    after = conn.pageInfo.endCursor;
    if (page === 39) throw new BoardIncomplete('stopped after 40 pages (2,000 items) without reaching the end');
  }
  if (items.length !== totalCount) {
    throw new BoardIncomplete(`GitHub counts ${totalCount} open items but ${items.length} were read`);
  }
  // A note may name only open items that are not projects, in this same read.
  const open = new Set(items.filter((i) => i.children_total === 0).map((i) => i.number));
  for (const it of items) {
    const note = notes.get(it.number);
    it.touches = note ? parseTouchesNote(note.body, note.createdAt, it.number, open) : null;
  }
  return { items, open_count: totalCount, read_at: new Date().toISOString() };
}

function toItem(n: Gql): BoardItem {
  // A list GitHub cut short is an error, not a shorter list.
  if (n.labels.totalCount > n.labels.nodes.length) {
    throw new BoardIncomplete(`item ${n.number} has ${n.labels.totalCount} labels; only ${n.labels.nodes.length} were read`);
  }
  if (n.assignees.totalCount > n.assignees.nodes.length) {
    throw new BoardIncomplete(`item ${n.number} has ${n.assignees.totalCount} assignees; only ${n.assignees.nodes.length} were read`);
  }
  if (n.subIssues.totalCount > n.subIssues.nodes.length) {
    throw new BoardIncomplete(`item ${n.number} has ${n.subIssues.totalCount} sub-items; only ${n.subIssues.nodes.length} were read`);
  }
  const children: BoardChild[] = (n.subIssues.nodes as Gql[]).map((c) => {
    if (c.labels.totalCount > c.labels.nodes.length) {
      throw new BoardIncomplete(`sub-item ${c.number} has more labels than were read`);
    }
    return {
      number: c.number,
      title: c.title,
      state: c.state === 'CLOSED' ? 'closed' : 'open',
      url: c.url,
      labels: (c.labels.nodes as Gql[]).map((l) => l.name),
    };
  });
  const comments = n.comments.nodes as Gql[];
  const c = comments[comments.length - 1];
  const commentBody: string = c?.body ?? '';
  const w = [...comments].reverse().find((x) => isWriteUp(x?.body));
  const assignees = (n.assignees.nodes as Gql[]).map((a) => a.login as string);
  const assignedAt = ((n.timelineItems?.nodes ?? []) as Gql[])
    .filter((e) => e?.createdAt && assignees.includes(e?.assignee?.login))
    .map((e) => e.createdAt as string)
    .sort();
  return {
    number: n.number,
    title: n.title,
    url: n.url,
    body: n.body ?? '',
    labels: (n.labels.nodes as Gql[]).map((l) => l.name),
    created_at: n.createdAt,
    updated_at: n.updatedAt,
    assignees,
    pinned_at: assignees.length ? (assignedAt[assignedAt.length - 1] ?? n.updatedAt) : null,
    parent: n.parent ? { number: n.parent.number, title: n.parent.title } : null,
    children,
    children_total: n.subIssuesSummary?.total ?? children.length,
    children_closed: n.subIssuesSummary?.completed ?? children.filter((x) => x.state === 'closed').length,
    latest_comment: c
      ? {
          excerpt: commentBody.slice(0, COMMENT_EXCERPT_CHARS),
          truncated: commentBody.length > COMMENT_EXCERPT_CHARS,
          created_at: c.createdAt,
          author: c.author?.login ?? null,
        }
      : null,
    write_up: w ? { body: String(w.body), created_at: w.createdAt, author: w.author?.login ?? null } : null,
    write_up_maybe_older: !w && n.comments.totalCount > comments.length,
    // Read in readItems, once every open item is known (a link to anything else is dropped).
    touches: null,
    checker: checkerOf(comments),
    builder_note: builderNoteOf(comments),
    approval: approvalOf(comments, FROM),
    dispatch: dispatchOf(comments),
    send_backs: sendBacksOf(comments, FROM),
  };
}

/** A decision write-up carries a "## Recommendation" heading of its own. */
export function isWriteUp(body: unknown): boolean {
  return typeof body === 'string' && /^##\s+Recommendation\b/m.test(body);
}

// ----- What the comments say about the work (items #1013 and #855) --------------------
//
// Read from the item's last WRITE_UP_LOOKBACK comments, like the write-up and the
// Touches note. Pure, so handler.test.ts reads real comments through them. Nothing
// here is guessed from a label: an item with no checker comment has `checker.last`
// null, and the page says so.

/** The checker's (Verifier's) newest word on the item's pull request. */
export interface CheckerWord {
  verdict: 'not ready' | 'ready';
  created_at: string;
  /** The Must-fix findings, one plain-words line each (the bold lead-in of each). */
  findings: string[];
  /** True when every finding is a check that did not run: nothing in the work to fix. */
  checks_only: boolean;
}

export interface Checker {
  /** How many passes the checker has made (its comments on the item, within the lookback). */
  passes: number;
  last: CheckerWord | null;
}

/** The checker's comment: its first line is "## Verifier …" (verifier.md). */
export const isCheckerComment = (body: unknown): boolean => typeof body === 'string' && /^##\s+Verifier\b/.test(body.trimStart());

/** The Builder's own note that a run pushed nothing (build-room-build.yml writes it). */
export const BUILDER_PUSHED_NOTHING = '## Builder — finished without pushing its work';
export const isBuilderNote = (body: unknown): boolean => typeof body === 'string' && body.trimStart().startsWith(BUILDER_PUSHED_NOTHING);

/** A fuller prompt a session posted for the item (PLAYBOOK §6: first line exactly "## Dispatch"). */
export const isDispatch = (body: unknown): boolean => typeof body === 'string' && /^##\s+Dispatch\s*$/.test(body.trimStart().split('\n')[0]);

/** A finding that is about a check that did not run, not about the work. */
const CHECK_DID_NOT_RUN = /never ran|did not run|didn.t run|not run\b|was skipped|skipped\b|action.required|never started|held back|could not run/i;

const stripMarks = (s: string) => s.replace(/\*\*|__|`/g, '').replace(/\s+/g, ' ').trim();

/** The Must-fix findings of a checker comment: one line each, in plain words. */
export function checkerFindings(body: string): string[] {
  const text = body.replace(/\r/g, '');
  const m = /^###\s+Must fix[^\n]*\n([\s\S]*?)(?=\n###\s|\nLabel:|$(?![\s\S]))/m.exec(text);
  if (!m) return [];
  const out: string[] = [];
  for (const raw of m[1].split('\n')) {
    const line = raw.trim();
    const item = /^(?:[-*]|\d+[.)])\s+(.*)$/.exec(line);
    if (!item) continue;
    const bold = /^\*\*(.+?)\*\*/.exec(item[1]);
    const lead = bold ? bold[1] : item[1];
    const words = stripMarks(lead).replace(/[.:]\s*$/, '');
    if (words) out.push(words.length > 220 ? `${words.slice(0, 217).trimEnd()}…` : words);
  }
  return out;
}

/** The checker's word, from one of its comments. */
export function checkerWord(body: string, createdAt: string): CheckerWord {
  const ready = /Label:\s*`?stage:ready-to-merge`?/.test(body);
  const findings = checkerFindings(body);
  const saidNothingToFix = /\*\*Nothing in the work to fix\.?\*\*/i.test(body);
  const checksOnly = !ready && (saidNothingToFix || (findings.length > 0 && findings.every((f) => CHECK_DID_NOT_RUN.test(f))));
  return { verdict: ready ? 'ready' : 'not ready', created_at: createdAt, findings, checks_only: checksOnly };
}

type CommentLike = { body?: string | null; createdAt?: string; author?: { login?: string | null } | null };

export function checkerOf(comments: readonly CommentLike[]): Checker {
  const mine = comments.filter((c) => isCheckerComment(c?.body));
  const last = mine[mine.length - 1];
  return { passes: mine.length, last: last ? checkerWord(String(last.body), String(last.createdAt ?? '')) : null };
}

/** The newest Builder note that a run pushed nothing, with its run link. */
export function builderNoteOf(comments: readonly CommentLike[]): { created_at: string; run_url: string | null } | null {
  const note = [...comments].reverse().find((c) => isBuilderNote(c?.body));
  if (!note) return null;
  const url = /https:\/\/github\.com\/\S+\/actions\/runs\/\d+/.exec(String(note.body ?? ''));
  return { created_at: String(note.createdAt ?? ''), run_url: url ? url[0] : null };
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** How Ben approved it: the command center's Approved comment, newest. */
export function approvalOf(comments: readonly CommentLike[], from: string): { created_at: string; words: string; route: 'cowork' | 'builder' | null } | null {
  const re = new RegExp(`^${escapeRe(from)}\\s+Approved`);
  const a = [...comments].reverse().find((c) => typeof c?.body === 'string' && re.test(c.body.trimStart()));
  if (!a) return null;
  const lines = String(a.body).replace(/\r/g, '').split('\n').map((l) => l.trim());
  const first = lines[0].replace(re, 'Approved').replace(/^Approved[:,]?\s*/, '').replace(/\.$/, '');
  const note = lines.find((l) => /^Ben's (note|words):/i.test(l));
  const words = [first, note ? note.replace(/^Ben's (note|words):\s*/i, '') : ''].filter(Boolean).join(' — ');
  const route = /^Route:\s*Builder/m.test(String(a.body)) ? 'builder' : /^Route:\s*Cowork/m.test(String(a.body)) ? 'cowork' : null;
  return { created_at: String(a.createdAt ?? ''), words, route };
}

/** The newest "## Dispatch" comment: a prompt a session wrote for the item. */
export function dispatchOf(comments: readonly CommentLike[]): { body: string; created_at: string } | null {
  const d = [...comments].reverse().find((c) => isDispatch(c?.body));
  if (!d) return null;
  const body = String(d.body).replace(/\r/g, '').split('\n').slice(1).join('\n').trim();
  return { body, created_at: String(d.createdAt ?? '') };
}

/** How many times Ben has sent it back to the Builder (the command center's own comment). */
export function sendBacksOf(comments: readonly CommentLike[], from: string): number {
  return comments.filter((c) => typeof c?.body === 'string' && c.body.trimStart().startsWith(`${from} Sent back to the Builder`)).length;
}

const isBuildRoom = (name: string | null | undefined) => /^Build Room/.test(name ?? '');

/**
 * Every run a query names, page by page. ⛔ Truncation is the failure guarded
 * against: stopping at the page cap with more pages to read is an error, never a
 * shorter list. But a run that changes state between GitHub's count and its list
 * makes "queued" or "in progress" count one more than it lists — 8 Oct, the live
 * smoke: "GitHub counts 1 runs for status=queued but 0 were read" — and that is
 * not a missing run: a short last page IS the end of the list.
 */
export async function listRuns(key: string, f: typeof fetch, query: string): Promise<Gql[]> {
  const out: Gql[] = [];
  let total = -1;
  let reachedEnd = false;
  for (let page = 1; page <= 20; page += 1) {
    const res = await f(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}/actions/runs?${query}&per_page=100&page=${page}`, {
      headers: ghHeaders(key),
    });
    await ghCheck(res);
    const body: Gql = await res.json();
    total = body.total_count;
    out.push(...(body.workflow_runs as Gql[]));
    if ((body.workflow_runs as Gql[]).length < 100 || out.length >= total) {
      reachedEnd = true;
      break;
    }
  }
  if (!reachedEnd) throw new BoardIncomplete(`GitHub counts ${total} runs for "${query}" but only ${out.length} were read before the page limit`);
  return out;
}

export async function readRuns(key: string, f: typeof fetch, now = new Date()): Promise<RunsView> {
  const since = new Date(now.getTime() - RUN_WINDOW_HOURS * 3600_000).toISOString();
  const [recent, running, queued, wfRes] = await Promise.all([
    listRuns(key, f, `created=${encodeURIComponent('>=' + since)}`),
    listRuns(key, f, 'status=in_progress'),
    listRuns(key, f, 'status=queued'),
    f(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}/actions/workflows?per_page=100`, { headers: ghHeaders(key) }),
  ]);
  await ghCheck(wfRes);
  const wfBody: Gql = await wfRes.json();
  if ((wfBody.workflows as Gql[]).length < wfBody.total_count) {
    throw new BoardIncomplete(`GitHub counts ${wfBody.total_count} workflows but ${wfBody.workflows.length} were read`);
  }
  // ⛔ A run's `name` is its run-name when the workflow sets one ("Assessor — item 535"),
  // not the workflow's name. Read the workflow by its id. Before this, every Assessor run
  // after #781 was dropped and Running now showed "Recon · 0" with eight running (8 Oct).
  const wfName = new Map<number, string>((wfBody.workflows as Gql[]).map((w) => [w.id as number, w.name as string]));
  const byId = new Map<number, BoardRun>();
  for (const r of [...recent, ...running, ...queued]) {
    const workflow = wfName.get(r.workflow_id) ?? r.name;
    if (!isBuildRoom(workflow)) continue;
    byId.set(r.id, {
      id: r.id,
      workflow,
      title: r.display_title ?? '',
      status: r.status,
      conclusion: r.conclusion ?? null,
      event: r.event,
      started_at: r.run_started_at ?? r.created_at,
      url: r.html_url,
    });
  }
  const runs = [...byId.values()].sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at));
  const workflows = (wfBody.workflows as Gql[])
    .filter((w) => isBuildRoom(w.name))
    .map((w) => ({ name: w.name as string, state: w.state as string }));
  return { runs, workflows, since, read_at: new Date().toISOString() };
}

// =====================================================================================
// Run 2 (item #702): who may act, the writes, and the feedback links.
// No label, field or value is added anywhere: every write uses the stage:*,
// priority:* and pushback:* labels that already exist and GitHub's own
// "assigned to" field (a pin, decision 6).
// =====================================================================================

export const ONLY_BEN = 'Only Ben acts from here. You can see everything.';
export const KEY_CANT_CONFIRM = "The board key can't confirm whose it is — its owner's GitHub profile shows no public email";
/** The Builder's workflow file. Approve with the Builder route starts it, and nothing else does (item #704). */
export const BUILDER_WORKFLOW = 'build-room-build.yml';
/** Every comment a write posts starts with this, so the board shows where it came from. */
export const FROM = 'From the command center:';
const FEEDBACK_LINK_MAX = 50;
const LAST_COMMENT_CHARS = 2000;

export const PUSHBACK_REASONS: Record<string, string> = {
  'check-again': 'Check again: something here is wrong',
  'not-real-options': "These aren't real options",
  'violates-values': 'This goes against how we work',
  'dont-understand': "I don't understand it",
  'tell-me-more': 'Tell me more',
};

/** The six priority labels, in the board's order (order-board.mjs). */
export const PRIORITY_WORDS: Record<string, string> = {
  'priority:money-wrong': 'Money is moving wrong',
  'priority:broken-for-user': 'Broken for someone using it',
  'priority:blocks-cutover': 'Blocks the punch cutover',
  'priority:decision-owed': 'A decision is owed',
  'priority:quality': 'Quality and debt',
  'priority:cosmetic': 'Cosmetic',
};

/** Each stage, worded to finish "this one is …". */
export const STAGE_WORDS: Record<string, string> = {
  'stage:intake': 'just filed',
  'stage:needs-recon': 'waiting for recon',
  'stage:needs-design': 'being designed',
  'stage:needs-decision': 'waiting for your decision',
  'stage:approved': 'approved',
  'stage:building': 'being built',
  'stage:needs-verify': 'being checked',
  'stage:ready-to-merge': 'ready to merge',
  'stage:ready-to-close': 'ready to close',
  'stage:needs-info': 'waiting on information the bots cannot read',
  'stage:parked': 'parked',
};

export const WRITE_ACTIONS = ['approve', 'pushback', 'park', 'unpark', 'priority', 'pin', 'unpin', 'merge', 'close', 'close-settled', 'keep-open', 'file', 'note', 'send-back', 'mark-building'] as const;
export type WriteAction = (typeof WRITE_ACTIONS)[number];

// ----- Filing a new item (item #853, pick 3A) ------------------------------------------
// The item form (.github/ISSUE_TEMPLATE/item.yml) puts one label on an item,
// stage:needs-recon, and intake (scripts/build-room/intake-check.mjs, TCSD-0445)
// refuses one that does not name a part of the app and a build route. The bridge
// refuses the same items BEFORE they are filed, by the same rule. Its key reads
// issues and runs, not the repository's files, so the catalog's part names are
// repeated here: handler.test.ts locks them to derive.ts's CATALOG_PARTS (which
// derive.test.ts locks to catalog/*.yml) and runs this copy and intakeVerdict on
// the same bodies, failing on any difference.

/** catalog/*.yml `name:` values, as CATALOG_PARTS in src/modules/build-room/derive.ts. */
export const FILE_PARTS: readonly string[] = [
  'Bids', 'Contracts', 'Account information collection (archived)', 'Account Management', 'EVSiQ',
  'Fleet and assets', 'Map', 'My Route', 'My Work', 'New accounts', 'Projects and billing communication',
  'Record routing', 'Supply', 'Tasks', 'Time and labour', 'E-Sign', 'Hiring', 'HR', 'Onboarding',
  'Partner application', 'Separations', 'Sourcing', 'Subcontractor onboarding portal',
  'Subcontractor replacement', 'Subcontractors', 'Annual increases', 'Incentives', 'Invoicing',
  'Money rules', 'Recent money changes', 'Admin', 'Ask AI', 'Dashboard', 'Design preview', 'Help chat',
  'Home', 'The app shell',
];
/** intake-check.mjs PIPELINE_PART and ROUTES. */
export const FILE_PIPELINE_PART = 'Build Room';
export const FILE_ROUTES = ['Builder', 'Cowork'] as const;
/** The one label the item form puts on an item. */
export const FILED_LABELS = ['stage:needs-recon'] as const;

const intakeClean = (s: string) => s.replace(/\*\*/g, '').replace(/`/g, '').trim();
function intakeField(body: string, label: string): string | null {
  const line = new RegExp(`^\\s*(?:[-*]\\s+)?\\**\\s*${label}\\s*\\**\\s*:\\s*\\**\\s*(.+)$`, 'im').exec(body);
  if (line) return intakeClean(line[1]);
  const form = new RegExp(`^###\\s*${label}\\s*\\n+([^\\n#][^\\n]*)`, 'im').exec(body);
  return form ? intakeClean(form[1]) : null;
}

/** intake-check.mjs intakeVerdict, for the items the bridge files. Same parse, same answer. */
export function intakeCopy(body: string): { ok: boolean; missing: string[]; parts: string[]; route: string | null } {
  const text = (body ?? '').replace(/\r/g, '');
  const known = new Map([...FILE_PARTS, FILE_PIPELINE_PART].map((n) => [n.toLowerCase(), n]));
  const missing: string[] = [];
  const partsRaw = intakeField(text, 'parts? of the app');
  const parts = partsRaw ? partsRaw.split(/[,;·]/).map((x) => x.trim().replace(/\.$/, '')).filter(Boolean) : [];
  const unknown = parts.filter((p) => !known.has(p.toLowerCase()));
  if (parts.length === 0) missing.push('the part of the app it changes');
  else if (unknown.length) missing.push(`a part of the app the catalog knows (not ${unknown.map((u) => `"${u}"`).join(', ')})`);
  const routeRaw = intakeField(text, 'build route');
  const route = routeRaw ? (FILE_ROUTES.find((r) => new RegExp(`^${r}\\b`, 'i').test(routeRaw)) ?? null) : null;
  if (!route) missing.push('a build route: Builder or Cowork');
  return { ok: missing.length === 0, missing, parts: parts.map((p) => known.get(p.toLowerCase()) ?? p), route };
}

/** The new item exactly as it will be filed — or why it will not be. Pure. */
export function planFile(b: Gql): { refuse: string } | { title: string; body: string; labels: string[]; wrote: string } {
  const title = String(b?.title ?? '').replace(/\s+/g, ' ').trim().slice(0, 200);
  if (!title) return { refuse: 'Give the item a title.' };
  const words = safeWords(b?.words);
  if (!words) return { refuse: 'Say what it is and why, in your words.' };
  const parts = (Array.isArray(b?.parts) ? b.parts : [b?.parts]).map((p: unknown) => String(p ?? '').trim()).filter(Boolean);
  // An empty line would let intake's pattern read the NEXT line as its value, so an empty part or route is refused here.
  if (parts.length === 0) return { refuse: 'Name the part of the app it changes.' };
  if (parts.some((p: string) => /[,;·\n]/.test(p))) return { refuse: 'Name each part of the app on its own.' };
  if (!String(b?.route ?? '').trim()) return { refuse: 'Say who builds it: Builder or Cowork.' };
  // Written in the catalog's own spelling when it is one; anything else is left as given, for intake's rule to refuse.
  const canon = new Map([...FILE_PARTS, FILE_PIPELINE_PART].map((n) => [n.toLowerCase(), n]));
  const named = parts.map((p: string) => canon.get(p.toLowerCase()) ?? p);
  const r = String(b?.route ?? '').trim();
  const route = FILE_ROUTES.find((x) => x.toLowerCase() === r.toLowerCase()) ?? r;
  const body = [`Part of the app: ${named.join(', ')}`, `Build route: ${route}`, '', `${FROM} ${words}`].join('\n');
  const v = intakeCopy(body);
  if (!v.ok) {
    return {
      refuse: `This item is missing ${v.missing.join(', and ')}. Parts of the app: ${[...FILE_PARTS].sort((x, y) => x.localeCompare(y)).join(' · ')} · ${FILE_PIPELINE_PART}.`,
    };
  }
  return { title, body, labels: [...FILED_LABELS], wrote: `Filed "${title}" (${v.parts.join(', ')}; ${v.route}). Intake checks it and the Assessor takes it from there.` };
}

type Caller = { authHeader: string; uid: string };
type OwnerCheck = { ok: true; login: string } | { ok: false; status: number; error: string; message: string };

class KeyCannotConfirm extends Error {}

const stageOf = (labels: readonly string[]) => labels.find((l) => l.startsWith('stage:')) ?? null;
const stageWords = (labels: readonly string[]) => {
  const s = stageOf(labels);
  return s ? (STAGE_WORDS[s] ?? s.slice('stage:'.length).replace(/-/g, ' ')) : 'at no stage';
};

/**
 * Ben's own words, made safe for the board. The merge gate reacts to ANY comment
 * containing a bare "/merge" (merge-on-bens-word.mjs), so one inside a note would
 * post a "Nothing was merged" reply. Wrapping it in backticks keeps the words and
 * stops the gate's pattern matching. Only the Merge button's own comment says it bare.
 */
export function safeWords(s: unknown): string {
  return String(s ?? '')
    .trim()
    .slice(0, 4000)
    .replace(/(^|\s)\/merge(?=\s|$)/gim, '$1`/merge`')
    // A heading is escaped, so words Ben gives can never be read as an item's write-up
    // ("## Recommendation", isWriteUp) or its Touches note — on every action that
    // carries them (the Assessor's and the Verifier's point on #853).
    .replace(/^([ \t]*)(#+)/gm, '$1\\$2');
}

/** The letter the write-up recommends (A, B, …), or null when it names none. */
export function recommendedLetter(writeUp: string | null | undefined): string | null {
  if (!writeUp) return null;
  const m = /^##\s+Recommendation\b[^\n]*\n([\s\S]*?)(?=^##\s|$(?![\s\S]))/m.exec(writeUp);
  const text = m ? m[1] : '';
  const hit =
    /\*\*(?:Option\s+)?([A-F])\b/.exec(text) ??
    /\bOption\s+([A-F])\b/.exec(text) ??
    /^\s*([A-F])\s*[—–:.)-]/m.exec(text);
  return hit ? hit[1] : null;
}

/**
 * Whose key is this? The key belongs to the ORGANIZATION as resource owner, and GitHub
 * offers the "Email addresses" permission only on a personal key — so the private
 * email list is out of reach by design (measured 2026-10-07). The key owner's PUBLIC
 * profile email is on GET /user for any key, with no permission at all. Ben chose that
 * route on 2026-10-07 (#702): his work email is public on his GitHub profile. No name,
 * no email, no second secret is written into the code — it follows whoever owns the key.
 */
async function keyOwner(key: string, f: typeof fetch): Promise<{ login: string; emails: string[] }> {
  const u = await f(`${GITHUB_API}/user`, { headers: ghHeaders(key) });
  await ghCheck(u);
  const body = (await u.json()) as Gql;
  const email = typeof body.email === 'string' ? body.email.trim().toLowerCase() : '';
  if (!email) throw new KeyCannotConfirm('the key owner shows no public email on GitHub');
  return { login: String(body.login ?? ''), emails: [email] };
}

/** Checks 2½ of every write: is the person signed in the owner of the key? Read fresh, every call. */
export async function ownerCheck(deps: BridgeDeps, key: string, caller: Caller): Promise<OwnerCheck> {
  if (!deps.callerEmail) return { ok: false, status: 500, error: 'cannot check', message: 'The bridge cannot check who is asking.' };
  if (!caller.uid) return { ok: false, status: 401, error: 'invalid_jwt', message: 'Your sign-in carries no user.' };
  const em = await deps.callerEmail(caller.authHeader, caller.uid);
  if (!em.ok) return { ok: false, status: em.status, error: 'cannot check', message: em.message };
  let owner: { login: string; emails: string[] };
  try {
    owner = await keyOwner(key, deps.fetch);
  } catch (e) {
    if (e instanceof KeyCannotConfirm) return { ok: false, status: 403, error: 'key cannot confirm', message: KEY_CANT_CONFIRM };
    throw e;
  }
  const mine = (em.value ?? '').trim().toLowerCase();
  if (!mine || !owner.emails.includes(mine)) return { ok: false, status: 403, error: 'not the key owner', message: ONLY_BEN };
  return { ok: true, login: owner.login };
}

async function gqlData(res: Response): Promise<Gql> {
  await ghCheck(res);
  const body: Gql = await res.json();
  if (body.errors?.length) {
    const first = body.errors[0];
    const msg = String(first?.message ?? 'unknown GraphQL error');
    if (first?.type === 'NOT_FOUND') return { ...body.data, __notFound: true };
    if (first?.type === 'FORBIDDEN' || /not accessible|permission/i.test(msg)) throw new GitHubRefused(403, msg);
    if (first?.type === 'RATE_LIMITED') throw new GitHubRateLimited(msg);
    throw new Error(`GitHub answered with an error: ${msg}`);
  }
  return body.data;
}

const gql = (key: string, f: typeof fetch, query: string, variables: Record<string, unknown>) =>
  f(`${GITHUB_API}/graphql`, {
    method: 'POST',
    headers: { ...ghHeaders(key), 'Content-Type': 'application/json', 'GraphQL-Features': 'sub_issues' },
    body: JSON.stringify({ query, variables }),
  });

/** One item, fresh from GitHub. Null when there is no such item. */
export async function readItem(key: string, f: typeof fetch, number: number): Promise<{ item: BoardItem; state: 'open' | 'closed' } | null> {
  const data = await gqlData(await gql(key, f, ITEM_QUERY, { owner: REPO_OWNER, name: REPO_NAME, number }));
  const n = data?.repository?.issue;
  if (!n) return null;
  return { item: toItem(n), state: n.state === 'CLOSED' ? 'closed' : 'open' };
}

type Change =
  | { kind: 'set-labels'; labels: string[] }
  | { kind: 'add-label'; label: string }
  | { kind: 'assign'; login: string }
  | { kind: 'unassign'; login: string }
  | { kind: 'close' }
  | { kind: 'none' };

type Plan = { refuse: string } | { comment: string | null; change: Change; what: string; wrote: string; startBuilder?: boolean };

const withStage = (labels: readonly string[], stage: string) => [...labels.filter((l) => !l.startsWith('stage:')), stage];

/** A bad request is refused before anything is read from GitHub. Returns the sentence, or null. */
export function validateWrite(action: string, b: Gql): string | null {
  if (b?.words !== undefined && typeof b.words !== 'string') return 'Words must be text.';
  switch (action) {
    case 'approve':
      if (b.choice !== 'recommendation' && b.choice !== 'own') return 'Say whether you approve the recommendation or your own option.';
      if (b.choice === 'own' && !safeWords(b.words)) return 'Say which option you mean, in your words.';
      if (b.route !== 'cowork' && b.route !== 'builder') return 'Say who builds it.';
      return null;
    case 'pushback':
      return PUSHBACK_REASONS[String(b.reason)] ? null : 'Pick one of the five reasons.';
    case 'priority':
      return PRIORITY_WORDS[String(b.to)] ? null : 'Pick one of the six priorities.';
    case 'close':
      if (!/^AFB-\d+$/.test(String(b.feedback_id ?? ''))) return 'Name the feedback report this closes for.';
      return safeWords(b.words) ? null : 'Say, in a few words, why it is closed.';
    case 'file': {
      if (b.title !== undefined && typeof b.title !== 'string') return 'The title must be text.';
      const p = planFile(b);
      return 'refuse' in p ? p.refuse : null;
    }
    case 'note':
      return safeWords(b.words) ? null : 'Say what to add, in your words.';
    default:
      return null;
  }
}

/** What a write will do to the item as GitHub has it now — or why it will not. Pure. */
export function planWrite(action: WriteAction, b: Gql, item: BoardItem, state: 'open' | 'closed', login: string): Plan {
  if (state === 'closed') return { refuse: 'This item is closed.' };
  const words = safeWords(b?.words);
  const stage = stageOf(item.labels);
  const where = stageWords(item.labels);
  switch (action) {
    case 'approve': {
      if (stage !== 'stage:needs-decision') return { refuse: `Approve works on an item waiting for your decision; this one is ${where}.` };
      const builder = b.route === 'builder';
      const route = builder ? 'Route: Builder — the bot builds it and opens a pull request for Ben to merge.' : 'Route: Cowork — a prompt Ben runs.';
      const after = builder ? 'Moved to approved, and the Builder has started.' : 'Moved to approved.';
      if (b.choice === 'recommendation') {
        if (!item.write_up) return { refuse: "There's no write-up on this item, so there's no recommendation to approve. Approve it in your words, or push back." };
        const letter = recommendedLetter(item.write_up.body);
        const rec = `the recommendation${letter ? ` (${letter})` : ''}`;
        return {
          comment: `${FROM} Approved: ${rec}.${words ? `\n\nBen's note: ${words}` : ''}\n\n${route}`,
          change: { kind: 'set-labels', labels: withStage(item.labels, 'stage:approved') },
          what: 'the move to approved',
          wrote: `Approved ${rec}, route ${builder ? 'Builder' : 'Cowork'}. ${after}`,
          startBuilder: builder,
        };
      }
      return {
        comment: `${FROM} Approved, in Ben's words: ${words}\n\n${route}`,
        change: { kind: 'set-labels', labels: withStage(item.labels, 'stage:approved') },
        what: 'the move to approved',
        wrote: `Approved in your words, route ${builder ? 'Builder' : 'Cowork'}. ${after}`,
        startBuilder: builder,
      };
    }
    case 'pushback': {
      if (stage !== 'stage:needs-decision') return { refuse: `Push back works on an item waiting for your decision; this one is ${where}.` };
      const label = `pushback:${b.reason}`;
      if (item.labels.includes(label)) return { refuse: 'It already carries that push-back; the Assessor has it.' };
      const reason = PUSHBACK_REASONS[String(b.reason)];
      return {
        comment: `${FROM} Pushed back — ${reason}.${words ? `\n\nBen's words: ${words}` : ''}\n\nThe Assessor rewrites the write-up with this reason.`,
        change: { kind: 'add-label', label },
        what: 'the push-back mark',
        wrote: `Sent back: ${reason}. The Assessor rewrites it.`,
      };
    }
    case 'park':
      if (stage === 'stage:parked') return { refuse: 'It is already parked.' };
      return {
        comment: `${FROM} Parked. It was ${where}.${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'set-labels', labels: withStage(item.labels, 'stage:parked') },
        what: 'the move to parked',
        wrote: 'Parked: it sits at the bottom of Needs you.',
      };
    case 'unpark': {
      if (stage !== 'stage:parked') return { refuse: `Unpark works on a parked item; this one is ${where}.` };
      const back = item.write_up ? 'stage:needs-decision' : 'stage:needs-recon';
      const why = item.write_up
        ? 'It has a write-up, so it goes back to waiting for your decision.'
        : 'It has no write-up yet, so it goes to recon to be written up.';
      return {
        comment: `${FROM} Unparked. ${why}${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'set-labels', labels: withStage(item.labels, back) },
        what: 'the move out of parked',
        wrote: item.write_up ? 'Unparked: back in Needs you.' : 'Unparked: sent to recon to be written up.',
      };
    }
    case 'priority': {
      const to = String(b.to);
      const was = item.labels.filter((l) => l.startsWith('priority:'));
      if (was.length === 1 && was[0] === to) return { refuse: 'It already has that priority.' };
      const wasWords = was.length ? was.map((l) => PRIORITY_WORDS[l] ?? l).join(' and ') : 'not prioritised';
      return {
        comment: `${FROM} Priority: ${wasWords} → ${PRIORITY_WORDS[to]}.`,
        change: { kind: 'set-labels', labels: [...item.labels.filter((l) => !l.startsWith('priority:')), to] },
        what: 'the priority change',
        wrote: `Priority: ${wasWords} → ${PRIORITY_WORDS[to]}.`,
      };
    }
    case 'pin':
      if (item.assignees.includes(login)) return { refuse: 'It is already pinned.' };
      return { comment: null, change: { kind: 'assign', login }, what: 'the pin', wrote: 'Pinned to the top.' };
    case 'unpin':
      if (!item.assignees.includes(login)) return { refuse: "It isn't pinned." };
      return { comment: null, change: { kind: 'unassign', login }, what: 'the unpin', wrote: 'Unpinned.' };
    case 'merge':
      if (stage !== 'stage:ready-to-merge') return { refuse: `Merge works on an item that is ready to merge; this one is ${where}.` };
      // The gate (merge-on-bens-word.mjs) does everything else and answers on the item.
      return {
        comment: `${FROM} /merge`,
        change: { kind: 'none' },
        what: 'the merge request',
        wrote: '"/merge" posted. The merge gate checks it and answers on the item.',
      };
    // Ready to close (item #827): the write-up's own finding is that nothing is left
    // to decide. Only Ben's press closes it; Keep it open sends it back to his calls.
    case 'close-settled':
      if (stage !== 'stage:ready-to-close') return { refuse: `Close works on an item that is ready to close; this one is ${where}.` };
      return {
        comment: `${FROM} Closed: nothing is left to decide, as the write-up says.${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'close' },
        what: 'the close',
        wrote: 'Closed.',
      };
    case 'keep-open':
      if (stage !== 'stage:ready-to-close') return { refuse: `Keep it open works on an item that is ready to close; this one is ${where}.` };
      return {
        comment: `${FROM} Kept open: it goes back to waiting for Ben's decision.${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'set-labels', labels: withStage(item.labels, 'stage:needs-decision') },
        what: 'the move back to your calls',
        wrote: 'Kept open: back in Your calls.',
      };
    // Item #853, pick 3A: Ben's words on any open item, at any stage. No label moves.
    // Its headings are escaped by safeWords, like every other action's words.
    case 'note':
      return {
        comment: `${FROM} ${words}`,
        change: { kind: 'none' },
        what: 'the note',
        wrote: 'Note added. Nothing else moved.',
      };
    // Item #1013 (Ben 28 Oct, option A): the checker sent the work back, or the
    // Builder stopped without pushing it, and Ben's press — nobody else's, and no
    // label — sends it back to the Builder (TCSD-0420 holds: the same actor guard as
    // Approve, through the same dispatch). The Builder works out which pass it is
    // from the item: being built with its own open pull request → a fix-it pass on
    // that branch; approved → a fresh build.
    case 'send-back': {
      const last = item.checker.last;
      const sentBackByChecker = stage === 'stage:building' && last?.verdict === 'not ready';
      const stoppedByBuilder = stage === 'stage:approved' && !!item.builder_note && Date.parse(item.builder_note.created_at) >= Date.parse(item.approval?.created_at ?? '0');
      if (sentBackByChecker && last?.checks_only) {
        return { refuse: 'The checker found nothing in the work to fix — only a check that did not run. There is nothing to send back; the checker runs again when the check does.' };
      }
      if (!sentBackByChecker && !stoppedByBuilder) {
        return { refuse: `Send back works on an item the checker sent back, or one the Builder stopped on without pushing its work; this one is ${where}.` };
      }
      const again = stoppedByBuilder;
      return {
        comment: again
          ? `${FROM} Start the Builder again.${words ? `\n\nBen's note: ${words}` : ''}`
          : `${FROM} Sent back to the Builder: it fixes the checker's findings on its own pull request.${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'none' },
        what: again ? 'the Builder restart' : 'the send-back',
        wrote: again ? 'The Builder has started again.' : "Sent back. The Builder is fixing the checker's findings on the same pull request; the checker runs again when it pushes.",
        startBuilder: true,
      };
    }
    // Item #855 (Ben 28 Oct, decision 1 option 1): a Cowork run starts from the Ready
    // to run column; this is the "Move the item to being built" its run line asks for.
    case 'mark-building':
      if (stage !== 'stage:approved') return { refuse: `Mark being built works on an approved item; this one is ${where}.` };
      return {
        comment: `${FROM} Being built — a Cowork run has started on it.${words ? `\n\nBen's note: ${words}` : ''}`,
        change: { kind: 'set-labels', labels: withStage(item.labels, 'stage:building') },
        what: 'the move to being built',
        wrote: 'Moved to being built. It leaves Ready to run and shows under Build and check.',
      };
    // Filing makes a new item (planFile); it never acts on an existing one.
    case 'file':
      return { refuse: 'Filing makes a new item; it does not act on an existing one.' };
    case 'close': {
      const id = String(b.feedback_id);
      if (!new RegExp(`\\b${id}\\b`).test(item.body)) return { refuse: `This item doesn't mention ${id}, so it isn't that report's item.` };
      return {
        comment: `${FROM} Closed with feedback ${id}: ${words}`,
        change: { kind: 'close' },
        what: 'the close',
        wrote: `Closed, with the words sent on ${id}.`,
      };
    }
  }
}

async function ghSend(key: string, f: typeof fetch, method: string, path: string, body: unknown): Promise<void> {
  const res = await f(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}${path}`, {
    method,
    headers: { ...ghHeaders(key), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ghCheck(res);
}

async function applyChange(key: string, f: typeof fetch, n: number, c: Change): Promise<void> {
  switch (c.kind) {
    case 'set-labels':
      return ghSend(key, f, 'PUT', `/issues/${n}/labels`, { labels: c.labels });
    case 'add-label':
      return ghSend(key, f, 'POST', `/issues/${n}/labels`, { labels: [c.label] });
    case 'assign':
      return ghSend(key, f, 'POST', `/issues/${n}/assignees`, { assignees: [c.login] });
    case 'unassign':
      return ghSend(key, f, 'DELETE', `/issues/${n}/assignees`, { assignees: [c.login] });
    case 'close':
      return ghSend(key, f, 'PATCH', `/issues/${n}`, { state: 'closed', state_reason: 'completed' });
    case 'none':
      return;
  }
}

const reasonOf = (e: unknown) =>
  e instanceof GitHubRefused ? `GitHub refused it (${e.status})` : e instanceof Error ? e.message.slice(0, 200) : String(e);

async function handleWrite(req: Request, deps: BridgeDeps, key: string, caller: Caller, headers: Record<string, string>): Promise<Response> {
  // The key's owner, before anything else is looked at.
  const who = await ownerCheck(deps, key, caller);
  if (!who.ok) return json({ error: who.error, message: who.message }, who.status, headers);

  let b: Gql;
  try {
    b = await req.json();
  } catch {
    return json({ error: 'bad request', message: 'The request had no readable body.' }, 400, headers);
  }
  const action = String(b?.action ?? '') as WriteAction;
  if (!(WRITE_ACTIONS as readonly string[]).includes(action)) {
    return json({ error: 'bad request', message: `There is no action called "${action}".` }, 400, headers);
  }
  if (action === 'file') {
    const bad = validateWrite(action, b);
    if (bad) return json({ error: 'bad request', message: bad }, 400, headers);
    const plan = planFile(b);
    if ('refuse' in plan) return json({ error: 'bad request', message: plan.refuse }, 400, headers);
    const res = await deps.fetch(`${GITHUB_API}/repos/${REPO_OWNER}/${REPO_NAME}/issues`, {
      method: 'POST',
      headers: { ...ghHeaders(key), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: plan.title, body: plan.body, labels: plan.labels }),
    });
    await ghCheck(res);
    const made = (await res.json()) as Gql;
    const filed = Number(made?.number);
    const after = Number.isInteger(filed) && filed > 0 ? await readItem(key, deps.fetch, filed) : null;
    return json(
      { ok: true, action, wrote: plan.wrote, at: new Date().toISOString(), signed_by: who.login, item: after?.item ?? null, item_state: after?.state ?? null },
      200,
      headers,
    );
  }

  const n = Number(b?.item);
  if (!Number.isInteger(n) || n <= 0) return json({ error: 'bad request', message: 'Name the item by its number.' }, 400, headers);
  const bad = validateWrite(action, b);
  if (bad) return json({ error: 'bad request', message: bad }, 400, headers);

  // Where the item is, read from GitHub now — never the page's copy.
  const current = await readItem(key, deps.fetch, n);
  if (!current) return json({ error: 'not found', message: `There is no item ${n}.` }, 404, headers);
  const plan = planWrite(action, b, current.item, current.state, who.login);
  if ('refuse' in plan) return json({ error: 'not where expected', message: plan.refuse }, 409, headers);

  // The comment first, then the labels.
  if (plan.comment) await ghSend(key, deps.fetch, 'POST', `/issues/${n}/comments`, { body: plan.comment });
  try {
    await applyChange(key, deps.fetch, n, plan.change);
  } catch (e) {
    if (!plan.comment) throw e;
    let noted = true;
    try {
      await ghSend(key, deps.fetch, 'POST', `/issues/${n}/comments`, {
        body: `${FROM} the change above did not apply — ${reasonOf(e)}. Nothing else moved.`,
      });
    } catch {
      noted = false;
    }
    return json(
      {
        error: 'partial write',
        message: `The comment was posted but ${plan.what} did not apply: ${reasonOf(e)}. ${noted ? 'A note saying so was added to the item.' : 'A note saying so could not be added either.'}`,
      },
      502,
      headers,
    );
  }

  // Approve with the Builder route starts the Builder, after the item is approved and
  // never before: the Builder refuses an item that is not approved.
  if (plan.startBuilder) {
    try {
      await ghSend(key, deps.fetch, 'POST', `/actions/workflows/${BUILDER_WORKFLOW}/dispatches`, { ref: 'main', inputs: { item: String(n) } });
    } catch (e) {
      let noted = true;
      try {
        await ghSend(key, deps.fetch, 'POST', `/issues/${n}/comments`, {
          body: `${FROM} ${plan.what === 'the move to approved' ? 'the item is approved, but ' : ''}the Builder did not start — ${reasonOf(e)}.`,
        });
      } catch {
        noted = false;
      }
      return json(
        {
          error: 'partial write',
          message: `${plan.what === 'the move to approved' ? 'It is approved, but the' : 'The'} Builder did not start: ${reasonOf(e)}. ${noted ? 'A note saying so was added to the item.' : 'A note saying so could not be added either.'}`,
        },
        502,
        headers,
      );
    }
  }

  const after = await readItem(key, deps.fetch, n);
  return json(
    {
      ok: true,
      action,
      wrote: plan.wrote,
      at: new Date().toISOString(),
      signed_by: who.login,
      item: after?.item ?? null,
      item_state: after?.state ?? null,
    },
    200,
    headers,
  );
}

// ----- feedback links ----------------------------------------------------------------

export interface FeedbackLink {
  number: number;
  title: string;
  url: string;
  closed_at: string | null;
  last_comment: { body: string; created_at: string; author: string | null } | null;
}

export interface FeedbackLinksView {
  links: Record<string, FeedbackLink[]>;
  read_at: string;
}

const CLOSED_QUERY = `query($owner:String!,$name:String!,$after:String){
  repository(owner:$owner,name:$name){
    issues(states:CLOSED,first:100,after:$after,orderBy:{field:CREATED_AT,direction:ASC}){
      totalCount
      pageInfo{hasNextPage endCursor}
      nodes{number title body url closedAt}
    }
  }
}`;

/** feedback-intake.mjs's rule: an item is a report's item when its body names the id. */
export async function readFeedbackLinks(key: string, f: typeof fetch, ids: string[]): Promise<FeedbackLinksView> {
  const wanted = new Set(ids);
  const links: Record<string, FeedbackLink[]> = Object.fromEntries(ids.map((i) => [i, []]));
  const matched = new Map<number, FeedbackLink>();
  let after: string | null = null;
  let read = 0;
  let total = -1;
  for (let page = 0; page < 40; page += 1) {
    const data = await gqlData(await gql(key, f, CLOSED_QUERY, { owner: REPO_OWNER, name: REPO_NAME, after }));
    const conn = data?.repository?.issues;
    if (!conn) throw new Error('GitHub returned no closed-item list for the repository');
    total = conn.totalCount;
    for (const n of conn.nodes as Gql[]) {
      read += 1;
      for (const m of String(n.body ?? '').matchAll(/\bAFB-\d+\b/g)) {
        if (!wanted.has(m[0])) continue;
        const link = matched.get(n.number) ?? { number: n.number, title: n.title, url: n.url, closed_at: n.closedAt ?? null, last_comment: null };
        matched.set(n.number, link);
        if (!links[m[0]].includes(link)) links[m[0]].push(link);
      }
    }
    if (!conn.pageInfo.hasNextPage) break;
    after = conn.pageInfo.endCursor;
    if (page === 39) throw new BoardIncomplete('stopped after 40 pages (4,000 closed items) without reaching the end');
  }
  if (read !== total) throw new BoardIncomplete(`GitHub counts ${total} closed items but ${read} were read`);

  // Each matched item's last comment: the draft resolution is taken from it. A Touches
  // note (item #703) says what an item touches, not what it did, so it is skipped.
  const nums = [...matched.keys()];
  for (let i = 0; i < nums.length; i += 50) {
    const chunk = nums.slice(i, i + 50);
    const q = `query($owner:String!,$name:String!){repository(owner:$owner,name:$name){${chunk
      .map((n) => `i${n}:issue(number:${n}){comments(last:3){nodes{body createdAt author{login}}}}`)
      .join(' ')}}}`;
    const data = await gqlData(await gql(key, f, q, { owner: REPO_OWNER, name: REPO_NAME }));
    for (const n of chunk) {
      const c = ([...(data?.repository?.[`i${n}`]?.comments?.nodes ?? [])] as Gql[]).reverse().find((x) => !isTouchesNote(x?.body));
      if (c) {
        matched.get(n)!.last_comment = { body: String(c.body).slice(0, LAST_COMMENT_CHARS), created_at: c.createdAt, author: c.author?.login ?? null };
      }
    }
  }
  for (const id of ids) links[id].sort((a, b) => Date.parse(b.closed_at ?? '') - Date.parse(a.closed_at ?? ''));
  return { links, read_at: new Date().toISOString() };
}
